// A machine's design choice on its branch page: the designs of its kind, what it
// inherits from the equipment profile, and its own override (an empty value is
// the organisation's default, taken on purpose).

import type { StoredLayout } from './api'
import { designFor, layoutKindFor, type ResolvedDesign } from './branchDesigns'
import { effective, sourceOf } from './equipment'
import { designChoices } from './kioskProfile'
import type { ProfileUnit, ProfileUnitKind, UnitInstance, UnitSettings } from './types'

/** The picker's value for "no choice of its own". */
export const FROM_PROFILE = '@profile'

export type DesignField = 'layoutId' | 'ticketLayoutId'

export interface MachineDesign {
  field: DesignField
  /** The designs this machine may choose. */
  choices: StoredLayout[]
  /** What it shows with no choice of its own. */
  inherited: ResolvedDesign
  /** What it shows now. */
  current: ResolvedDesign
  /** FROM_PROFILE, '' (the default, chosen) or a design id. */
  value: string
}

/** The design fields a kind has on its branch page: a screen, and a kiosk's slip. */
export function designFields(kind: ProfileUnitKind): DesignField[] {
  if (kind === 'kiosk') return ['layoutId', 'ticketLayoutId']
  return layoutKindFor(kind) ? ['layoutId'] : []
}

export function machineDesign(
  field: DesignField,
  kind: ProfileUnitKind,
  unit: Pick<ProfileUnit, 'settings'>,
  own: UnitSettings,
  defaults: UnitSettings,
  layouts: readonly StoredLayout[],
  operationProfileId: string,
): MachineDesign {
  const ticket = field === 'ticketLayoutId'
  const settings = unit.settings ?? {}
  const pick = designChoices(layouts, kind, operationProfileId)
  const resolve = (o: UnitSettings) =>
    designFor(kind, effective(defaults, settings, o), sourceOf(field, settings, o), layouts, operationProfileId, ticket)
  return {
    field,
    choices: ticket ? pick.ticket : pick.screen,
    inherited: resolve({}),
    current: resolve(own),
    value: own[field] ?? FROM_PROFILE,
  }
}

/** The instance with its own choice set, or dropped back to the profile's. */
export function withDesign(inst: UnitInstance, field: DesignField, value: string): UnitInstance {
  const overrides = { ...(inst.overrides ?? {}) }
  if (value === FROM_PROFILE) delete overrides[field]
  else overrides[field] = value
  return { ...inst, overrides }
}
